import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';

import { PrescriptionTypes } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { map, take } from 'rxjs';

import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import { Appointment, AppointmentEventPlaceType, AppointmentProviderType, Visit, VisitStatusTypes } from '@lib/core/models/visit.interface';

import { InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

import { VisitDialogLabels } from '../../../../core/models/medical-entity-modal.const';
import { VisitsService } from '../../../../core/services/visits.service';
import { AppointmentManagementModalComponent } from '../../modals/appointment-management-modal/appointment-management-modal.component';
import { VisitViewModalComponent } from '../../modals/visit-view-modal/visit-view-modal.component';

const COLUMN_HEADERS = {
  CLIENTS_NAME: 'clientsName',
  APPOINTMENT_TYPE: 'appointmentType',
  APPOINTMENT_NAME: 'appointmentName',
  STATUS: 'status',
  DATE_TIME: 'dateTime',
  DOCTORS_NAME: 'doctorsName',
  ACTIONS: 'actions',
};

const BASE_COLUMNS = [
  COLUMN_HEADERS.APPOINTMENT_TYPE,
  COLUMN_HEADERS.APPOINTMENT_NAME,
  COLUMN_HEADERS.STATUS,
  COLUMN_HEADERS.DATE_TIME,
  COLUMN_HEADERS.DOCTORS_NAME,
  COLUMN_HEADERS.ACTIONS,
];

const DEFAULT_DIALOG_CONFIG: Partial<MatDialogConfig> = {
  height: '100%',
  minWidth: '600px',
  width: '50%',
  position: { right: '0' },
};

@Component({
  selector: 'hm-appointments-table',
  standalone: true,
  imports: [MatIcon, StatusComponent, TranslatePipe, MatTableModule, NgClass, InfoBadgeComponent],
  templateUrl: './appointments-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentsTableComponent {
  private readonly router = inject(Router);
  private readonly visitsService = inject(VisitsService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  readonly appointments = input.required<Visit[]>();
  readonly withName = input(false);
  readonly listUpdated = output<boolean>();
  readonly sortChanged = output<{ sortBy: string | null; sortDirection: 'asc' | 'desc' | null }>();

  protected readonly columns = computed(() => (this.withName() ? [COLUMN_HEADERS.CLIENTS_NAME, ...BASE_COLUMNS] : BASE_COLUMNS));

  protected currentSortBy: string | null = 'appointment_date';
  protected currentSortDirection: 'asc' | 'desc' | null = 'desc';

  protected readonly columnHeaders = COLUMN_HEADERS;
  protected readonly appointmentProviderType = AppointmentProviderType;
  protected readonly eventPlaceTypes = AppointmentEventPlaceType;
  protected readonly formatDate = formatDate;

  protected redirectToClientsProfile(appointmentId: string): void {
    this.visitsService
      .getMemberProfileFromAction(appointmentId)
      .pipe(take(1))
      .subscribe({
        next: member => this.router.navigate([`/members/${member.id}`]),
        error: () => this.snackBar.open('Не вдалось здійснити перехід.', 'OK', { duration: 5000 }),
      });
  }

  private createVisitDialogData(appointment: Visit, prescriptions?: string[]) {
    return {
      userId: appointment.userId,
      guaranteeId: appointment.systemMetadata?.guarantee_application_id ?? null,
      serviceType: appointment.metadata.type,
      serviceName: prescriptions ? VisitDialogLabels.drugsServiceDefaultName : appointment.metadata.name,
      status: appointment.status,
      doctorSpecialty: appointment.metadata.doctor_specialties,
      doctorName: appointment.metadata.doctor_name,
      visitDate: appointment.appointmentDate,
      clinicName: appointment.metadata.partner_name ?? null,
      location: prescriptions ? `${appointment.metadata.name}, ${appointment.metadata.location}` : appointment.metadata.location,
      orderNumber: appointment.metadata.order_number,
      expirationDate: appointment.expirationDate,
      ...(prescriptions && { prescriptions }),
    };
  }

  protected showAppointmentDetails(appointment: Visit): void {
    if (appointment.metadata.type === PrescriptionTypes.Drugs && appointment.id && appointment.metadata.order_id) {
      this.visitsService
        .getPharmacyOrderByActionId(appointment.id)
        .pipe(
          map(items => items.products.map(product => `${product.name}[${product.quantityInPackage}од/уп]`)),
          take(1)
        )
        .subscribe({
          next: prescriptions => {
            this.dialog.open(VisitViewModalComponent, {
              ...DEFAULT_DIALOG_CONFIG,
              data: this.createVisitDialogData(appointment, prescriptions),
            });
          },
          error: error => console.error('Error loading pharmacy order details:', error),
        });
    } else {
      this.dialog.open(VisitViewModalComponent, {
        ...DEFAULT_DIALOG_CONFIG,
        data: this.createVisitDialogData(appointment),
      });
    }
  }

  private openAppointmentManagementModal(appointment: Visit | Appointment, actionType: ModalActionTypes): void {
    this.dialog
      .open(AppointmentManagementModalComponent, {
        width: '720px',
        data: { actionType, appointment },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => this.listUpdated.emit(!!result));
  }

  protected editManualAppointment(appointment: Appointment): void {
    this.openAppointmentManagementModal(appointment, ModalActionTypes.Edit);
  }

  protected deleteManualAppointment(appointment: Visit): void {
    this.openAppointmentManagementModal(appointment, ModalActionTypes.Delete);
  }

  protected showEditButton(appointment: Visit): boolean {
    const appointmentStartDate = new Date(appointment.appointmentDate);
    return appointment.metadata.provider === this.appointmentProviderType.Unknown && new Date() < appointmentStartDate && appointment.status === VisitStatusTypes.ToDo;
  }

  protected onDateColumnSort(): void {
    if (this.withName()) return;

    const sortBy = 'appointment_date';

    if (this.currentSortBy !== sortBy) {
      this.currentSortBy = sortBy;
      this.currentSortDirection = 'desc';
    } else this.currentSortDirection = this.currentSortDirection === 'desc' ? 'asc' : 'desc';

    this.sortChanged.emit({
      sortBy: this.currentSortBy,
      sortDirection: this.currentSortDirection,
    });
  }
}
